import { z } from 'zod';

/**
 * Article body is an ordered list of blocks, stored as JSONB on `articles.body`.
 * This schema is the single source of truth for that shape — used by the editor
 * (client) and validated again server-side before persistence.
 */

export const blockTypeSchema = z.enum([
  'paragraph',
  'h2',
  'h3',
  'quote',
  'image',
  'callout',
  'divider',
  /** Text beside an image; the only block that carries its own layout. */
  'split',
]);
export type BlockType = z.infer<typeof blockTypeSchema>;

/** Which side the image sits on in a `split` block (text takes the other side). */
export const imagePositionSchema = z.enum(['left', 'right']);
export type ImagePosition = z.infer<typeof imagePositionSchema>;

export const blockSchema = z
  .object({
    id: z.string().min(1),
    type: blockTypeSchema,
    content: z.string().default(''),
    url: z.string().url().optional(),
    caption: z.string().optional(),
    /** `split` only; defaults to 'right' when omitted. */
    imagePosition: imagePositionSchema.optional(),
  })
  .strict();
export type Block = z.infer<typeof blockSchema>;

/**
 * Inline text blocks support lightweight markdown (`**bold**`, `*italic*`,
 * `` `code` ``, `[link](url)`). Headings and captions are rendered as plain text.
 */
export const INLINE_MARKDOWN_TYPES: readonly BlockType[] = [
  'paragraph',
  'quote',
  'callout',
  'split',
];

export const articleBodySchema = z.array(blockSchema);
export type ArticleBody = z.infer<typeof articleBodySchema>;

/** Average adult reading speed used to estimate read time. */
const WORDS_PER_MINUTE = 200;

/** Plain-text projection of a body, for read-time + search indexing. */
export function blocksToPlainText(body: ArticleBody): string {
  return body
    .map((b) => [b.content, b.caption].filter(Boolean).join(' '))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function estimateReadTimeMinutes(body: ArticleBody, extraText = ''): number {
  const words = `${extraText} ${blocksToPlainText(body)}`.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / WORDS_PER_MINUTE));
}
